import { useEffect, useRef, useState } from "react";
import { toast } from "sonner";
import { Camera, FileText, Loader2, Trash2, Upload } from "lucide-react";
import { api, openDownload } from "@/lib/api";
import { AuthedImage } from "@/components/AuthedImage";
import { Button } from "@/components/ui/button";

const MAX_EDGE = 1600;
const QUALITY = 0.8;

/** Shrinks a picture to a sensible size before upload — ID text stays readable. */
const compress = (file) =>
  new Promise((resolve, reject) => {
    if (!file.type.startsWith("image/")) return resolve(file);
    const reader = new FileReader();
    reader.onload = () => {
      const img = new Image();
      img.onload = () => {
        const scale = Math.min(1, MAX_EDGE / Math.max(img.width, img.height));
        const canvas = document.createElement("canvas");
        canvas.width = Math.round(img.width * scale);
        canvas.height = Math.round(img.height * scale);
        canvas.getContext("2d").drawImage(img, 0, 0, canvas.width, canvas.height);
        canvas.toBlob(
          (blob) =>
            blob
              ? resolve(new File([blob], `${(file.name || "photo").replace(/\.\w+$/, "")}.jpg`, {
                  type: "image/jpeg",
                }))
              : reject(new Error("compress failed")),
          "image/jpeg",
          QUALITY
        );
      };
      img.onerror = reject;
      img.src = reader.result;
    };
    reader.onerror = reject;
    reader.readAsDataURL(file);
  });

export const PhotoCapture = ({ items, onChange, required = false, testid = "photo-capture" }) => {
  const [busy, setBusy] = useState(false);
  const [camOpen, setCamOpen] = useState(false);
  const videoRef = useRef(null);
  const streamRef = useRef(null);
  const fileRef = useRef(null);

  useEffect(
    () => () => streamRef.current?.getTracks().forEach((t) => t.stop()),
    []
  );

  const send = async (file, kind) => {
    setBusy(true);
    try {
      const prepared = await compress(file);
      const form = new FormData();
      form.append("file", prepared);
      const { data } = await api.post("/uploads", form, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      onChange([
        ...items,
        {
          file_id: data.file_id,
          kind,
          filename: data.filename || prepared.name,
          content_type: data.content_type || prepared.type,
          size: prepared.size,
          label: "",
        },
      ]);
      toast.success(
        `Saved (${(prepared.size / 1024).toFixed(0)} KB)`
      );
    } catch (err) {
      toast.error(
        (err.response?.data?.detail && String(err.response.data.detail)) ||
          "Could not save that file"
      );
    } finally {
      setBusy(false);
    }
  };

  const openCamera = async () => {
    try {
      const stream = await navigator.mediaDevices.getUserMedia({
        video: { facingMode: "environment", width: { ideal: 1920 } },
      });
      streamRef.current = stream;
      setCamOpen(true);
      setTimeout(() => {
        if (videoRef.current) videoRef.current.srcObject = stream;
      }, 50);
    } catch (e) {
      toast.error("No camera available on this device — attach a file instead");
    }
  };

  const closeCamera = () => {
    streamRef.current?.getTracks().forEach((t) => t.stop());
    streamRef.current = null;
    setCamOpen(false);
  };

  const shoot = async () => {
    const video = videoRef.current;
    if (!video) return;
    const canvas = document.createElement("canvas");
    canvas.width = video.videoWidth;
    canvas.height = video.videoHeight;
    canvas.getContext("2d").drawImage(video, 0, 0);
    const blob = await new Promise((res) => canvas.toBlob(res, "image/jpeg", 0.92));
    closeCamera();
    if (blob) await send(new File([blob], "id-photo.jpg", { type: "image/jpeg" }), "id_photo");
  };

  const remove = (i) => onChange(items.filter((_, j) => j !== i));

  return (
    <div data-testid={testid}>
      <div className="flex flex-wrap items-center gap-2">
        <Button
          type="button"
          variant="outline"
          size="sm"
          className="rounded-none"
          onClick={openCamera}
          disabled={busy}
          data-testid={`${testid}-camera-button`}
        >
          <Camera className="mr-1.5 h-4 w-4" /> Take photo
        </Button>
        <Button
          type="button"
          variant="outline"
          size="sm"
          className="rounded-none"
          onClick={() => fileRef.current?.click()}
          disabled={busy}
          data-testid={`${testid}-attach-button`}
        >
          <Upload className="mr-1.5 h-4 w-4" /> Attach file
        </Button>
        {busy && <Loader2 className="h-4 w-4 animate-spin text-muted-foreground" />}
        <input
          ref={fileRef}
          type="file"
          accept="image/*,application/pdf"
          className="hidden"
          onChange={(e) => {
            const f = e.target.files?.[0];
            e.target.value = "";
            if (f) send(f, f.type === "application/pdf" ? "document" : "id_photo");
          }}
          data-testid={`${testid}-file-input`}
        />
      </div>

      {required && items.length === 0 && (
        <p className="mt-2 text-xs font-semibold text-[#9A3412]" data-testid={`${testid}-required-warning`}>
          At least one customer ID photo or document is required before this contract can be saved.
        </p>
      )}

      {camOpen && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/80 p-4" data-testid={`${testid}-camera-dialog`}>
          <div className="w-full max-w-lg bg-white p-4">
            <video ref={videoRef} autoPlay playsInline muted className="w-full bg-black" />
            <div className="mt-3 flex gap-2">
              <Button type="button" variant="outline" className="flex-1 rounded-none" onClick={closeCamera} data-testid={`${testid}-camera-cancel`}>
                Cancel
              </Button>
              <Button
                type="button"
                className="flex-1 rounded-none bg-[#C5A059] font-semibold text-[#0A0A0A] hover:bg-[#B38F4E]"
                onClick={shoot}
                data-testid={`${testid}-camera-shoot`}
              >
                Capture
              </Button>
            </div>
          </div>
        </div>
      )}

      {items.length > 0 && (
        <div className="mt-3 flex flex-wrap gap-3">
          {items.map((a, i) => (
            <div key={a.file_id} className="w-[120px]" data-testid={`${testid}-item-${i}`}>
              <button
                type="button"
                onClick={() =>
                  openDownload(`/files/${a.file_id}`).catch(() =>
                    toast.error("Could not open that file")
                  )
                }
                className="block w-full border border-border"
                data-testid={`${testid}-open-${i}`}
              >
                {a.content_type === "application/pdf" ? (
                  <div className="flex h-[90px] flex-col items-center justify-center gap-1 bg-muted text-xs">
                    <FileText className="h-6 w-6 text-muted-foreground" /> PDF
                  </div>
                ) : (
                  <AuthedImage
                    fileId={a.file_id}
                    alt={a.filename || "attachment"}
                    className="h-[90px] w-full object-cover"
                  />
                )}
              </button>
              <div className="mt-1 flex items-center justify-between gap-1">
                <span className="truncate text-[10px] text-muted-foreground">
                  {a.size ? `${(a.size / 1024).toFixed(0)} KB` : a.kind}
                </span>
                <button
                  type="button"
                  onClick={() => remove(i)}
                  className="text-muted-foreground transition-colors duration-150 hover:text-destructive"
                  data-testid={`${testid}-remove-${i}`}
                  aria-label="Remove attachment"
                >
                  <Trash2 className="h-3.5 w-3.5" />
                </button>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

export default PhotoCapture;
